/**
 * Voice Input Component
 * Speech-to-text for hands-free interaction
 */

import React, { useState, useEffect, useRef } from 'react';
import './VoiceInput.css';

const VoiceInput = ({ onTranscript, disabled }) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState(null);
  
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);

  // Initialize speech recognition
  useEffect(() => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      setError('Speech recognition not supported in this browser. Try Chrome or Edge.');
      return;
    }

    setIsSupported(true);

    // Create recognition instance
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    // Event handlers
    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
      setTranscript('');
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptPart = event.results[i][0].transcript;
        
        if (event.results[i].isFinal) {
          finalTranscript += transcriptPart + ' ';
        } else {
          interimTranscript += transcriptPart;
        }
      }

      setTranscript(finalTranscript || interimTranscript);

      // Auto-stop after 2 seconds of silence
      if (finalTranscript) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          recognition.stop();
        }, 2000);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      
      const errorMessages = {
        'no-speech': 'No speech detected. Please try again.',
        'audio-capture': 'Microphone not accessible. Check permissions.',
        'not-allowed': 'Microphone permission denied.',
        'network': 'Network error. Check your connection.',
        'aborted': 'Speech recognition aborted.',
      };

      setError(errorMessages[event.error] || 'Speech recognition error. Please try again.');
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      
      // Send transcript if we have one
      if (transcript.trim()) {
        onTranscript(transcript.trim());
        setTranscript('');
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      clearTimeout(timeoutRef.current);
    };
  }, [transcript, onTranscript]);

  const handleToggle = () => {
    if (!isSupported) {
      return;
    }

    if (isListening) {
      // Stop listening
      recognitionRef.current?.stop();
    } else {
      // Start listening
      try {
        recognitionRef.current?.start();
      } catch (error) {
        console.error('Failed to start recognition:', error);
        setError('Failed to start speech recognition. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    setTranscript('');
    recognitionRef.current?.stop();
  };

  if (!isSupported && error) {
    return (
      <div className="voice-input-error">
        <span className="voice-error-icon">🎤</span>
        <span className="voice-error-text">{error}</span>
      </div>
    );
  }

  return (
    <div className="voice-input-container">
      <button
        className={`voice-button ${isListening ? 'listening' : ''}`}
        onClick={handleToggle}
        disabled={disabled || !isSupported}
        title={isListening ? 'Stop recording' : 'Start voice input'}
      >
        {isListening ? (
          <>
            <span className="voice-icon recording">🎤</span>
            <span className="voice-pulse"></span>
          </>
        ) : (
          <span className="voice-icon">🎤</span>
        )}
      </button>

      {isListening && (
        <div className="voice-recording-indicator">
          <div className="recording-visual">
            <div className="sound-wave"></div>
            <div className="sound-wave"></div>
            <div className="sound-wave"></div>
          </div>
          
          {transcript && (
            <div className="live-transcript">
              <p>{transcript}</p>
            </div>
          )}
          
          <button 
            className="cancel-recording"
            onClick={handleCancel}
            title="Cancel recording"
          >
            Cancel
          </button>
        </div>
      )}

      {error && (
        <div className="voice-error-toast">
          {error}
        </div>
      )}
    </div>
  );
};

export default VoiceInput;
